import React, {
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  FiCheckCircle,
  FiFacebook,
  FiInstagram,
  FiLoader,
  FiLogOut,
  FiRefreshCw,
  FiLink,
  FiShield,
  FiArrowRight,
  FiAlertCircle,
  FiUsers,
  FiDollarSign,
} from "react-icons/fi";

import api from "../../utills/api";

const Meta = () => {
  /*
   * ============================================================
   * STATE
   * ============================================================
   */

  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const [loadingPages, setLoadingPages] = useState(false);
  const [loadingAdAccounts, setLoadingAdAccounts] = useState(false);

  const [connectingPage, setConnectingPage] = useState(false);
  const [connectingInstagram, setConnectingInstagram] = useState(false);
  const [connectingAdAccount, setConnectingAdAccount] =
    useState(false);

  const [status, setStatus] = useState(null);
  const [metaUser, setMetaUser] = useState(null);

  const [pages, setPages] = useState([]);
  const [adAccounts, setAdAccounts] = useState([]);

  const [instagram, setInstagram] = useState(null);

  const [selectedPageId, setSelectedPageId] = useState("");
  const [selectedAdAccountId, setSelectedAdAccountId] =
    useState("");

  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  /*
   * ============================================================
   * OAUTH QUERY PARAMETERS
   * ============================================================
   *
   * Education backend redirects with:
   *
   * ?meta=connected&pages=pending
   *
   * or
   *
   * ?meta=error&message=...
   */

  const searchParams = new URLSearchParams(
    window.location.search
  );

  const oauthResult = searchParams.get("meta");
  const oauthPages = searchParams.get("pages");
  const oauthMessage = searchParams.get("message");

  // Instagram Business Login callback parameters.
  const instagramOAuthResult = searchParams.get("instagram");
  const instagramOAuthMessage = searchParams.get("message");

  /*
   * ============================================================
   * HELPERS
   * ============================================================
   */

  const getPageId = useCallback((page) => {
    return (
      page?.id ||
      page?.pageId ||
      page?._id ||
      ""
    );
  }, []);

  const getPageName = useCallback((page) => {
    return (
      page?.name ||
      page?.pageName ||
      "Facebook Page"
    );
  }, []);

  const getAdAccountId = useCallback((account) => {
    return (
      account?.id ||
      account?.accountId ||
      ""
    );
  }, []);

  const getAdAccountName = useCallback((account) => {
    return (
      account?.name ||
      account?.accountName ||
      account?.accountId ||
      account?.id ||
      "Meta Ad Account"
    );
  }, []);

  const formatDate = (dateValue) => {
    if (!dateValue) {
      return "Never";
    }

    const parsedDate = new Date(dateValue);

    if (Number.isNaN(parsedDate.getTime())) {
      return "Never";
    }

    return parsedDate.toLocaleString();
  };

  /*
   * ============================================================
   * CONNECTION STATE
   * ============================================================
   */

  const connectionStatus =
    status?.connectionStatus ||
    "Disconnected";

  const isConnected =
    connectionStatus === "Connected" &&
    status?.connected !== false;

  /*
   * IMPORTANT:
   *
   * Education backend uses Pending when:
   *
   * Meta OAuth completed
   *          ↓
   * Facebook Page still needs selection
   */

  const isPending =
    connectionStatus === "Pending";

  const isError =
    connectionStatus === "Error";

  const isTokenExpired =
    connectionStatus === "Token Expired";

  const facebookConnected =
    status?.facebook?.connected === true ||
    Boolean(status?.facebook?.pageId);

  const instagramConnected =
    status?.instagram?.connected === true ||
    Boolean(status?.instagram?.accountId) ||
    Boolean(instagram?.accountId);

  const adAccountConnected =
    status?.ads?.connected === true ||
    Boolean(status?.ads?.adAccountId);

  const webhookSubscribed =
    status?.facebook?.webhookSubscribed === true;

  /*
   * ============================================================
   * STATUS LABEL
   * ============================================================
   */

  const getStatusLabel = () => {
    if (isConnected) {
      return "Connected";
    }

    if (isPending) {
      return "Action Required";
    }

    if (isTokenExpired) {
      return "Token Expired";
    }

    if (isError) {
      return "Connection Error";
    }

    return "Not Connected";
  };

  const getStatusClass = () => {
    if (isConnected) {
      return "bg-success-subtle text-success";
    }

    if (isPending) {
      return "bg-warning-subtle text-warning";
    }

    if (isTokenExpired || isError) {
      return "bg-danger-subtle text-danger";
    }

    return "bg-secondary-subtle text-secondary";
  };

  /*
   * ============================================================
   * LOAD META STATUS
   * ============================================================
   */

  const loadMetaStatus = useCallback(async () => {
    try {
      const response = await api.get("/meta/status");

      const data =
        response?.data?.data || null;

      setStatus(data);

      return data;
    } catch (err) {
      console.error(
        "Failed to load Meta status:",
        err
      );

      throw err;
    }
  }, []);

  /*
   * ============================================================
   * LOAD META USER
   * ============================================================
   */

  const loadMetaUser = useCallback(async () => {
    try {
      const response =
        await api.get("/meta/me");

      setMetaUser(
        response?.data?.data || null
      );
    } catch (err) {
      console.error(
        "Failed to load Meta user:",
        err
      );

      setMetaUser(null);
    }
  }, []);

  /*
   * ============================================================
   * LOAD FACEBOOK PAGES
   * ============================================================
   */

  const loadPages = useCallback(async () => {
    try {
      setLoadingPages(true);

      const response =
        await api.get("/meta/pages");

      const data =
        response?.data?.data;

      const pageList =
        Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.pages)
          ? data.pages
          : [];

      setPages(pageList);

      /*
       * Select currently connected Page automatically.
       */

      const connectedPage =
        pageList.find(
          (page) =>
            String(
              getPageId(page)
            ) ===
            String(
              status?.facebook?.pageId || ""
            )
        );

      if (connectedPage) {
        setSelectedPageId(
          String(
            getPageId(connectedPage)
          )
        );
      } else if (
        pageList.length === 1
      ) {
        /*
         * If only one Page exists,
         * preselect it.
         */
        setSelectedPageId(
          String(
            getPageId(pageList[0])
          )
        );
      }
    } catch (err) {
      console.error(
        "Failed to load Facebook Pages:",
        err
      );

      setPages([]);

      throw err;
    } finally {
      setLoadingPages(false);
    }
  }, [getPageId, status?.facebook?.pageId]);

  /*
   * ============================================================
   * LOAD INSTAGRAM
   * ============================================================
   */

  const loadInstagram = useCallback(
    async (currentStatus = status) => {
      const accountId =
        currentStatus?.instagram
          ?.accountId;

      if (!accountId) {
        setInstagram(null);
        return;
      }

      try {
        const response =
          await api.get("/meta/instagram");

        setInstagram(
          response?.data?.data || {
            accountId,
            username:
              currentStatus?.instagram
                ?.username || "",
          }
        );
      } catch (err) {
        console.error(
          "Failed to load Instagram:",
          err
        );

        /*
         * Keep the information already
         * returned by /meta/status.
         */

        setInstagram({
          connected: true,
          accountId,
          username:
            currentStatus?.instagram
              ?.username || "",
        });
      }
    },
    [status]
  );

  /*
   * ============================================================
   * SYNC INSTAGRAM FROM CONNECTED FACEBOOK PAGE
   * ============================================================
   *
   * The Education backend discovers the Page-linked Instagram
   * account when /meta/pages/select (or /meta/pages/connect)
   * runs. Re-selecting the already-connected Page is therefore
   * the correct way to refresh Instagram after the admin links
   * Instagram to the Page in Meta Business Suite.
   */

  const syncInstagramFromPage = useCallback(
    async (pageId, { silent = false } = {}) => {
      if (!pageId) {
        return false;
      }

      try {
        const response = await api.post(
          "/meta/pages/select",
          { pageId }
        );

        const refreshedStatus =
          await loadMetaStatus();

        if (refreshedStatus?.instagram?.accountId) {
          await loadInstagram(refreshedStatus);

          if (!silent) {
            setMessage(
              response?.data?.message ||
                "Instagram account discovered and connected successfully."
            );
          }

          return true;
        }

        if (!silent) {
          setError(
            "Your Facebook Page is connected, but Meta has not returned an Instagram Professional account linked to this Page."
          );
        }

        return false;
      } catch (err) {
        console.error(
          "Instagram Page-linked discovery failed:",
          err
        );

        if (!silent) {
          setError(
            err?.response?.data?.message ||
              err?.message ||
              "Unable to discover the Instagram account linked to this Facebook Page."
          );
        }

        return false;
      }
    },
    [loadInstagram, loadMetaStatus]
  );

  /*
   * ============================================================
   * CONNECT / SYNC INSTAGRAM
   * ============================================================
   */

  const handleConnectInstagram = async () => {
    if (!facebookConnected) {
      setError(
        "Connect a Facebook Page first, then connect Instagram."
      );
      return;
    }

    try {
      setConnectingInstagram(true);
      setError("");
      setMessage("");

      // IMPORTANT: this button starts the dedicated Instagram
      // Business Login flow. Re-selecting the Facebook Page is
      // kept for the separate Discover action because a Page
      // linked Instagram may not be exposed through the Page
      // access token with the permissions originally granted.
      const response = await api.get("/meta/instagram/start");
      const authorizationUrl =
        response?.data?.data?.authorizationUrl;

      if (!authorizationUrl) {
        throw new Error(
          "Instagram authorization URL was not returned by the server."
        );
      }

      window.location.assign(authorizationUrl);
    } catch (err) {
      console.error(
        "Failed to start Instagram Business Login:",
        err
      );

      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to start Instagram connection."
      );
      setConnectingInstagram(false);
    }
  };

  const handleDiscoverInstagram = async () => {
    const pageId =
      status?.facebook?.pageId ||
      status?.facebookPageId ||
      "";

    if (!pageId) {
      setError(
        "Connect a Facebook Page first, then discover Instagram."
      );
      return;
    }

    try {
      setConnectingInstagram(true);
      setError("");
      setMessage("");

      const connected = await syncInstagramFromPage(pageId);

      if (connected) {
        const refreshedStatus = await loadMetaStatus();
        await loadInstagram(refreshedStatus);
      }
    } finally {
      setConnectingInstagram(false);
    }
  };

  /*
   * ============================================================
   * LOAD AD ACCOUNTS
   * ============================================================
   */

  const loadAdAccounts = useCallback(async () => {
    try {
      setLoadingAdAccounts(true);

      const response =
        await api.get(
          "/meta/ad-accounts"
        );

      const data =
        response?.data?.data;

      const accountList =
        Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.adAccounts)
          ? data.adAccounts
          : [];

      setAdAccounts(accountList);

      /*
       * Select currently connected Ad Account.
       */

      const currentAdAccount =
        status?.ads?.adAccountId;

      if (currentAdAccount) {
        setSelectedAdAccountId(
          String(currentAdAccount)
        );
      } else if (
        accountList.length === 1
      ) {
        setSelectedAdAccountId(
          String(
            getAdAccountId(
              accountList[0]
            )
          )
        );
      }
    } catch (err) {
      console.error(
        "Failed to load Meta Ad Accounts:",
        err
      );

      setAdAccounts([]);

      throw err;
    } finally {
      setLoadingAdAccounts(false);
    }
  }, [
    getAdAccountId,
    status?.ads?.adAccountId,
  ]);

  /*
   * ============================================================
   * INITIALIZE
   * ============================================================
   */

  useEffect(() => {
    const initialize = async () => {
      try {
        setLoading(true);
        setError("");

        /*
         * Handle OAuth callback.
         */

        if (oauthResult === "connected") {
          setMessage(
            "Meta account authorized successfully. Please select a Facebook Page."
          );
        }

        if (oauthResult === "error") {
          setError(
            oauthMessage ||
              "Meta authorization failed."
          );
        }

        if (oauthResult === "cancelled") {
          setError(
            oauthMessage ||
              "Meta authorization was cancelled."
          );
        }

        if (instagramOAuthResult === "connected") {
          setMessage(
            "Instagram connected successfully. Loading the Instagram account..."
          );
        }

        if (instagramOAuthResult === "error") {
          setError(
            instagramOAuthMessage ||
              "Instagram authorization failed."
          );
        }

        if (instagramOAuthResult === "cancelled") {
          setError(
            instagramOAuthMessage ||
              "Instagram authorization was cancelled."
          );
        }

        /*
         * Remove OAuth query parameters.
         */

        if (
          oauthResult ||
          oauthPages ||
          oauthMessage ||
          instagramOAuthResult ||
          instagramOAuthMessage
        ) {
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname
          );
        }

        /*
         * Load status first.
         */

        const currentStatus =
          await loadMetaStatus();

        if (!currentStatus) {
          return;
        }

        const currentConnectionStatus =
          currentStatus?.connectionStatus;

        const connected =
          currentConnectionStatus ===
            "Connected" &&
          currentStatus?.connected !== false;

        const pending =
          currentConnectionStatus ===
          "Pending";

        /*
         * If Meta is authorized or connected,
         * load the Meta user.
         */

        if (connected || pending) {
          await loadMetaUser();
        }

        /*
         * Pages are required for Pending state.
         *
         * They are also useful when Connected
         * because the user may want to see the
         * available Pages.
         */

        if (connected || pending) {
          try {
            await loadPages();
          } catch (pageError) {
            if (pending) {
              setError(
                pageError?.response
                  ?.data?.message ||
                  "Unable to load Facebook Pages."
              );
            }
          }
        }

        /*
         * Instagram.
         *
         * If the Facebook Page is connected but Instagram was
         * linked later in Meta Business Suite, re-select the same
         * Page once so the backend can discover and persist the
         * Page-linked Instagram Professional account.
         */

        if (
          currentStatus?.instagram
            ?.accountId
        ) {
          await loadInstagram(
            currentStatus
          );
        }

        // If Instagram OAuth just completed, the callback has already
        // persisted the account. Refresh status/profile without trying
        // to re-select the Facebook Page.
        if (instagramOAuthResult === "connected") {
          const refreshedInstagramStatus =
            await loadMetaStatus();
          if (refreshedInstagramStatus?.instagram?.accountId) {
            await loadInstagram(refreshedInstagramStatus);
          }
        }

        /*
         * Ad Accounts.
         *
         * Only load when Meta has been
         * authorized.
         */

        if (connected || pending) {
          try {
            await loadAdAccounts();
          } catch (adError) {
            /*
             * Ad Account failure should not
             * break the entire Meta screen.
             */
            console.warn(
              "Unable to load Meta Ad Accounts:",
              adError
            );
          }
        }
      } catch (err) {
        console.error(
          "Failed to initialize Meta:",
          err
        );

        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load Meta integration."
        );
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, []);

  /*
   * ============================================================
   * REFRESH
   * ============================================================
   */

  const handleRefresh = async () => {
    try {
      setLoading(true);
      setError("");

      const currentStatus =
        await loadMetaStatus();

      if (
        currentStatus?.connected ||
        currentStatus?.connectionStatus ===
          "Connected" ||
        currentStatus?.connectionStatus ===
          "Pending"
      ) {
        await loadMetaUser();

        try {
          await loadPages();
        } catch (err) {
          console.warn(
            "Unable to refresh Pages:",
            err
          );
        }

        if (
          currentStatus?.instagram
            ?.accountId
        ) {
          await loadInstagram(
            currentStatus
          );
        } else if (
          currentStatus?.facebook?.pageId
        ) {
          await syncInstagramFromPage(
            currentStatus.facebook.pageId,
            { silent: true }
          );
        }

        try {
          await loadAdAccounts();
        } catch (err) {
          console.warn(
            "Unable to refresh Ad Accounts:",
            err
          );
        }
      } else {
        setMetaUser(null);
        setPages([]);
        setInstagram(null);
        setAdAccounts([]);
        setSelectedPageId("");
        setSelectedAdAccountId("");
      }
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to refresh Meta integration."
      );
    } finally {
      setLoading(false);
    }
  };

  /*
   * ============================================================
   * START META OAUTH
   * ============================================================
   */

  const handleConnect = async () => {
    try {
      setActionLoading(true);
      setError("");
      setMessage("");

      const response =
        await api.get(
          "/meta/oauth/start"
        );

      const authorizationUrl =
        response?.data?.data
          ?.authorizationUrl;

      if (!authorizationUrl) {
        throw new Error(
          "Meta authorization URL was not returned."
        );
      }

      window.location.href =
        authorizationUrl;
    } catch (err) {
      console.error(
        "Meta connection start failed:",
        err
      );

      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to start Meta connection."
      );

      setActionLoading(false);
    }
  };

  /*
   * ============================================================
   * CONNECT FACEBOOK PAGE
   * ============================================================
   */

  const handleConnectPage = async () => {
    if (!selectedPageId) {
      setError(
        "Please select a Facebook Page."
      );

      return;
    }

    try {
      setConnectingPage(true);
      setError("");
      setMessage("");

      const response =
        await api.post(
          "/meta/pages/connect",
          {
            pageId: selectedPageId,
          }
        );

      setMessage(
        response?.data?.message ||
          "Facebook Page connected successfully."
      );

      /*
       * Refresh everything after
       * Page connection.
       */

      const currentStatus =
        await loadMetaStatus();

      await loadMetaUser();
      await loadPages();

      if (
        currentStatus?.instagram
          ?.accountId
      ) {
        await loadInstagram(
          currentStatus
        );
      }

      /*
       * Refresh Ad Accounts.
       */

      try {
        await loadAdAccounts();
      } catch (err) {
        console.warn(
          "Unable to refresh Ad Accounts:",
          err
        );
      }
    } catch (err) {
      console.error(
        "Facebook Page connection failed:",
        err
      );

      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to connect Facebook Page."
      );
    } finally {
      setConnectingPage(false);
    }
  };

  /*
   * ============================================================
   * CONNECT AD ACCOUNT
   * ============================================================
   */

  const handleConnectAdAccount = async () => {
    if (!selectedAdAccountId) {
      setError(
        "Please select a Meta Ad Account."
      );

      return;
    }

    try {
      setConnectingAdAccount(true);
      setError("");
      setMessage("");

      const response =
        await api.post(
          "/meta/ad-account",
          {
            adAccountId:
              selectedAdAccountId,
          }
        );

      setMessage(
        response?.data?.message ||
          "Meta Ad Account connected successfully."
      );

      await loadMetaStatus();
      await loadAdAccounts();
    } catch (err) {
      console.error(
        "Meta Ad Account connection failed:",
        err
      );

      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to connect Meta Ad Account."
      );
    } finally {
      setConnectingAdAccount(false);
    }
  };

  /*
   * ============================================================
   * DISCONNECT AD ACCOUNT
   * ============================================================
   */

  const handleDisconnectAdAccount =
    async () => {
      const confirmed =
        window.confirm(
          "Are you sure you want to disconnect the Meta Ad Account?"
        );

      if (!confirmed) {
        return;
      }

      try {
        setConnectingAdAccount(true);
        setError("");
        setMessage("");

        const response =
          await api.delete(
            "/meta/ad-account"
          );

        setMessage(
          response?.data?.message ||
            "Meta Ad Account disconnected successfully."
        );

        setSelectedAdAccountId("");

        await loadMetaStatus();
        await loadAdAccounts();
      } catch (err) {
        console.error(
          "Meta Ad Account disconnect failed:",
          err
        );

        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to disconnect Meta Ad Account."
        );
      } finally {
        setConnectingAdAccount(false);
      }
    };

  /*
   * ============================================================
   * SUBSCRIBE LEADGEN
   * ============================================================
   */

  const handleSubscribeLeadgen =
    async () => {
      try {
        setActionLoading(true);
        setError("");
        setMessage("");

        const response =
          await api.post(
            "/meta/pages/subscribe-leadgen"
          );

        setMessage(
          response?.data?.message ||
            "Facebook Leadgen webhook subscription completed."
        );

        await loadMetaStatus();
      } catch (err) {
        console.error(
          "Leadgen subscription failed:",
          err
        );

        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to subscribe Facebook Leadgen."
        );
      } finally {
        setActionLoading(false);
      }
    };

  /*
   * ============================================================
   * DISCONNECT META
   * ============================================================
   */

  const handleDisconnect = async () => {
    const confirmed =
      window.confirm(
        "Are you sure you want to disconnect Meta? This will remove the connected Facebook Page, Instagram account and Ad Account."
      );

    if (!confirmed) {
      return;
    }

    try {
      setActionLoading(true);
      setError("");
      setMessage("");

      const response =
        await api.post(
          "/meta/disconnect"
        );

      setMessage(
        response?.data?.message ||
          "Meta integration disconnected successfully."
      );

      setStatus(null);
      setMetaUser(null);
      setPages([]);
      setInstagram(null);
      setAdAccounts([]);

      setSelectedPageId("");
      setSelectedAdAccountId("");

      /*
       * Reload status after disconnect
       * so the UI returns to the initial
       * state.
       */

      await loadMetaStatus();
    } catch (err) {
      console.error(
        "Meta disconnect failed:",
        err
      );

      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to disconnect Meta."
      );
    } finally {
      setActionLoading(false);
    }
  };

  /*
   * ============================================================
   * RENDER
   * ============================================================
   */

  return (
    <div className="page-content">
      <div className="container-fluid">

        {/* ======================================================
            HEADER
        ======================================================= */}

        <div className="d-flex align-items-center justify-content-between mb-4">

          <div>
            <h4 className="mb-1">
              Meta Integration
            </h4>

            <p className="text-muted mb-0">
              Connect Facebook and Instagram to capture
              education leads from Meta Lead Ads.
            </p>
          </div>

          <button
            type="button"
            className="btn btn-light"
            onClick={handleRefresh}
            disabled={loading}
          >
            <FiRefreshCw
              className={`me-1 ${
                loading
                  ? "spin-animation"
                  : ""
              }`}
            />

            Refresh
          </button>

        </div>

        {/* ======================================================
            SUCCESS
        ======================================================= */}

        {message && (
          <div
            className="alert alert-success alert-dismissible"
            role="alert"
          >
            <FiCheckCircle className="me-2" />

            {message}

            <button
              type="button"
              className="btn-close"
              onClick={() =>
                setMessage("")
              }
            />
          </div>
        )}

        {/* ======================================================
            ERROR
        ======================================================= */}

        {error && (
          <div
            className="alert alert-danger alert-dismissible"
            role="alert"
          >
            <FiAlertCircle className="me-2" />

            {error}

            <button
              type="button"
              className="btn-close"
              onClick={() =>
                setError("")
              }
            />
          </div>
        )}

        {/* ======================================================
            LOADING
        ======================================================= */}

        {loading ? (
          <div className="card shadow-sm border-0">

            <div className="card-body text-center py-5">

              <FiLoader
                size={32}
                className="text-primary mb-3"
              />

              <h5>
                Loading Meta integration...
              </h5>

              <p className="text-muted mb-0">
                Checking your organization's Meta connection.
              </p>

            </div>

          </div>
        ) : (
          <>
            {/* ==================================================
                MAIN META CARD
            ================================================== */}

            <div className="card shadow-sm border-0">

              <div className="card-body">

                {/* ==============================================
                    HEADER
                =============================================== */}

                <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">

                  <div className="d-flex align-items-center">

                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center me-3"
                      style={{
                        width: 48,
                        height: 48,
                        background:
                          "rgba(13, 110, 253, 0.1)",
                      }}
                    >
                      <FiFacebook
                        className="text-primary"
                        size={25}
                      />
                    </div>

                    <div>

                      <h5 className="mb-1">
                        Facebook & Instagram
                      </h5>

                      <div className="text-muted fs-13">
                        Meta Lead Ads integration
                        for education enquiries.
                      </div>

                    </div>

                  </div>

                  <span
                    className={`badge ${getStatusClass()} px-3 py-2`}
                  >
                    {getStatusLabel()}
                  </span>

                </div>

                {/* ==============================================
                    NOT CONNECTED
                =============================================== */}

                {!isConnected &&
                  !isPending && (
                    <>

                      <div className="border rounded p-4">

                        <div className="d-flex align-items-start">

                          <div className="me-3">

                            <div
                              className="rounded-circle d-flex align-items-center justify-content-center"
                              style={{
                                width: 52,
                                height: 52,
                                background:
                                  "rgba(13, 110, 253, 0.1)",
                              }}
                            >
                              <FiFacebook
                                className="text-primary"
                                size={27}
                              />
                            </div>

                          </div>

                          <div className="flex-grow-1">

                            <h5 className="mb-1">
                              Connect Facebook & Instagram
                            </h5>

                            <p className="text-muted mb-3">
                              Authorize your organization's
                              Meta account to access Facebook
                              Pages, Instagram business
                              information and Meta Lead Ads.
                            </p>

                            <div className="alert alert-light border mb-4">

                              <div className="d-flex align-items-start">

                                <FiShield
                                  size={20}
                                  className="text-primary me-2 flex-shrink-0"
                                />

                                <div>

                                  <strong>
                                    Administrator access required
                                  </strong>

                                  <div className="text-muted fs-13 mt-1">
                                    Only an organization Admin
                                    can connect or disconnect
                                    the Meta integration.
                                  </div>

                                </div>

                              </div>

                            </div>

                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={handleConnect}
                              disabled={
                                actionLoading
                              }
                            >
                              {actionLoading ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                  />

                                  Connecting...
                                </>
                              ) : (
                                <>
                                  <FiFacebook className="me-2" />

                                  Connect Meta
                                </>
                              )}
                            </button>

                          </div>

                        </div>

                      </div>

                    </>
                  )}

                {/* ==============================================
                    PENDING / PAGE SELECTION
                =============================================== */}

                {isPending && (
                  <>

                    <div className="alert alert-info d-flex align-items-start">

                      <FiFacebook
                        size={21}
                        className="me-2 flex-shrink-0"
                      />

                      <div>

                        <strong>
                          Meta account authorized
                        </strong>

                        <div className="fs-13 mt-1">
                          Your Meta account has been
                          authorized. Select the Facebook
                          Page that should send education
                          lead enquiries to your organization.
                        </div>

                      </div>

                    </div>

                    {/* ==========================================
                        FACEBOOK PAGE SELECTION
                    =========================================== */}

                    <div className="border rounded p-4">

                      <div className="d-flex align-items-center justify-content-between mb-3">

                        <div>

                          <h6 className="mb-1">
                            Facebook Page
                          </h6>

                          <p className="text-muted fs-13 mb-0">
                            Select the Page associated with
                            your education business.
                          </p>

                        </div>

                        <span className="badge bg-primary-subtle text-primary">
                          {pages.length}{" "}
                          {pages.length === 1
                            ? "Page"
                            : "Pages"}
                        </span>

                      </div>

                      {loadingPages ? (
                        <div className="text-center py-4">

                          <span
                            className="spinner-border spinner-border-sm text-primary"
                          />

                          <div className="text-muted fs-13 mt-2">
                            Loading your Facebook Pages...
                          </div>

                        </div>
                      ) : pages.length === 0 ? (
                        <div className="text-center py-4">

                          <FiFacebook
                            size={34}
                            className="text-muted mb-3"
                          />

                          <h6>
                            No Facebook Pages found
                          </h6>

                          <p className="text-muted fs-13">
                            The connected Meta account
                            does not currently have any
                            available Facebook Pages.
                          </p>

                          <button
                            type="button"
                            className="btn btn-light"
                            onClick={loadPages}
                            disabled={loadingPages}
                          >
                            <FiRefreshCw className="me-1" />

                            Reload Pages
                          </button>

                        </div>
                      ) : (
                        <>

                          <select
                            className="form-select"
                            value={selectedPageId}
                            onChange={(event) =>
                              setSelectedPageId(
                                event.target.value
                              )
                            }
                            disabled={
                              connectingPage
                            }
                          >

                            <option value="">
                              Select a Facebook Page
                            </option>

                            {pages.map(
                              (page) => (
                                <option
                                  key={
                                    getPageId(page)
                                  }
                                  value={
                                    getPageId(page)
                                  }
                                >
                                  {getPageName(page)}
                                </option>
                              )
                            )}

                          </select>

                          <div className="border rounded p-3 bg-light mt-3">

                            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">

                              <div>

                                <div className="fw-semibold">
                                  Ready to connect
                                </div>

                                <div className="text-muted fs-13 mt-1">
                                  The selected Page will
                                  receive the Meta lead
                                  webhook for your organization.
                                </div>

                              </div>

                              <button
                                type="button"
                                className="btn btn-primary"
                                onClick={
                                  handleConnectPage
                                }
                                disabled={
                                  !selectedPageId ||
                                  connectingPage
                                }
                              >

                                {connectingPage ? (
                                  <>
                                    <span
                                      className="spinner-border spinner-border-sm me-2"
                                      role="status"
                                    />

                                    Connecting Page...
                                  </>
                                ) : (
                                  <>
                                    <FiLink className="me-1" />

                                    Connect Page
                                  </>
                                )}

                              </button>

                            </div>

                          </div>

                        </>
                      )}

                    </div>

                  </>
                )}

                {/* ==============================================
                    CONNECTED
                =============================================== */}

                {isConnected && (
                  <>

                    <div className="alert alert-success d-flex align-items-start">

                      <FiCheckCircle
                        size={21}
                        className="me-2 flex-shrink-0"
                      />

                      <div>

                        <strong>
                          Meta is connected
                        </strong>

                        <div className="fs-13 mt-1">
                          Your Facebook Page is connected
                          and your organization can receive
                          supported Meta education lead enquiries.
                        </div>

                      </div>

                    </div>

                    {/* ==========================================
                        CONNECTED ACCOUNTS
                    =========================================== */}

                    <div className="border rounded p-4">

                      <div className="row">

                        {/* FACEBOOK */}

                        <div className="col-xl-4 col-md-6 mb-4 mb-xl-0">

                          <div className="d-flex align-items-start">

                            <div className="me-3">

                              <FiFacebook
                                className="text-primary"
                                size={30}
                              />

                            </div>

                            <div>

                              <div className="text-muted fs-12 mb-1">
                                Facebook Page
                              </div>

                              <div className="fw-semibold">
                                {status?.facebook
                                  ?.pageName ||
                                  "Facebook Page"}
                              </div>

                              <div className="text-muted fs-12 mt-1">
                                {status?.facebook
                                  ?.pageId ||
                                  "—"}
                              </div>

                              <div className="mt-2">

                                <span className="badge bg-success-subtle text-success">
                                  Connected
                                </span>

                              </div>

                            </div>

                          </div>

                        </div>

                        {/* INSTAGRAM */}

                        <div className="col-xl-4 col-md-6 mb-4 mb-xl-0">

                          <div className="d-flex align-items-start">

                            <div className="me-3">

                              <FiInstagram
                                className={
                                  instagramConnected
                                    ? "text-danger"
                                    : "text-warning"
                                }
                                size={30}
                              />

                            </div>

                            <div className="flex-grow-1">

                              <div className="text-muted fs-12 mb-1">
                                Instagram
                              </div>

                              {instagramConnected ? (
                                <>
                                  <div className="fw-semibold">
                                    {instagram?.username ||
                                      status?.instagram?.username
                                      ? `@${
                                          instagram?.username ||
                                          status?.instagram?.username
                                        }`
                                      : "Instagram Professional account"}
                                  </div>

                                  <div className="text-muted fs-12 mt-1">
                                    {instagram?.accountId ||
                                      status?.instagram?.accountId ||
                                      "—"}
                                  </div>

                                  <div className="mt-2">
                                    <span className="badge bg-success-subtle text-success">
                                      Connected
                                    </span>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div className="text-muted fs-13 mb-2">
                                    No Instagram Professional account is connected yet.
                                  </div>

                                  <div className="d-flex flex-wrap align-items-center gap-2">
                                    <button
                                      type="button"
                                      className="btn btn-primary btn-sm"
                                      onClick={handleConnectInstagram}
                                      disabled={connectingInstagram || !facebookConnected}
                                    >
                                      {connectingInstagram ? (
                                        <>
                                          <span
                                            className="spinner-border spinner-border-sm me-1"
                                            role="status"
                                          />
                                          Connecting...
                                        </>
                                      ) : (
                                        <>
                                          <FiInstagram className="me-1" />
                                          Connect Instagram
                                        </>
                                      )}
                                    </button>

                                    <button
                                      type="button"
                                      className="btn btn-outline-secondary btn-sm"
                                      onClick={handleDiscoverInstagram}
                                      disabled={connectingInstagram || !facebookConnected}
                                    >
                                      <FiLink className="me-1" />
                                      Discover
                                    </button>

                                    <span className="badge bg-warning-subtle text-warning">
                                      Not Connected
                                    </span>
                                  </div>

                                  <div className="text-muted fs-12 mt-2">
                                    Use Connect Instagram for Instagram Business Login, or Discover to find an Instagram Professional account already linked to this Facebook Page.
                                  </div>
                                </>
                              )}

                            </div>

                          </div>

                        </div>

                        {/* LEADGEN */}

                        <div className="col-xl-4 col-md-6">

                          <div className="d-flex align-items-start">

                            <div className="me-3">

                              <FiUsers
                                className={
                                  webhookSubscribed
                                    ? "text-success"
                                    : "text-warning"
                                }
                                size={30}
                              />

                            </div>

                            <div>

                              <div className="text-muted fs-12 mb-1">
                                Lead Generation
                              </div>

                              <div className="fw-semibold">
                                Facebook Leadgen
                              </div>

                              <div className="text-muted fs-13 mt-1">
                                Lead webhook subscription
                              </div>

                              <div className="mt-2">

                                <span
                                  className={`badge ${
                                    webhookSubscribed
                                      ? "bg-success-subtle text-success"
                                      : "bg-warning-subtle text-warning"
                                  }`}
                                >
                                  {webhookSubscribed
                                    ? "Subscribed"
                                    : "Not Subscribed"}
                                </span>

                              </div>

                            </div>

                          </div>

                        </div>

                      </div>

                      <hr className="my-4" />

                      {/* ========================================
                          AD ACCOUNT
                      ========================================= */}

                      <div>

                        <div className="d-flex align-items-start">

                          <div className="me-3">

                            <FiDollarSign
                              className="text-primary"
                              size={30}
                            />

                          </div>

                          <div className="flex-grow-1">

                            <div className="text-muted fs-12 mb-1">
                              Meta Ad Account
                            </div>

                            {adAccountConnected ? (
                              <>

                                <div className="fw-semibold">
                                  {status?.ads
                                    ?.adAccountName ||
                                    status?.ads
                                      ?.adAccountId ||
                                    "Meta Ad Account"}
                                </div>

                                <div className="text-muted fs-12 mt-1">
                                  Account ID:{" "}
                                  {status?.ads
                                    ?.adAccountId ||
                                    "—"}
                                </div>

                                {status?.ads
                                  ?.currency && (
                                  <div className="text-muted fs-12 mt-1">
                                    Currency:{" "}
                                    {
                                      status.ads
                                        .currency
                                    }
                                  </div>
                                )}

                                <div className="mt-2">

                                  <span className="badge bg-success-subtle text-success">
                                    Connected
                                  </span>

                                </div>

                                <div className="mt-3">

                                  <button
                                    type="button"
                                    className="btn btn-outline-danger btn-sm"
                                    onClick={
                                      handleDisconnectAdAccount
                                    }
                                    disabled={
                                      connectingAdAccount
                                    }
                                  >

                                    {connectingAdAccount ? (
                                      <>
                                        <span
                                          className="spinner-border spinner-border-sm me-1"
                                          role="status"
                                        />

                                        Disconnecting...
                                      </>
                                    ) : (
                                      <>
                                        <FiLogOut className="me-1" />

                                        Disconnect Ad Account
                                      </>
                                    )}

                                  </button>

                                </div>

                              </>
                            ) : (
                              <>

                                <div className="text-muted fs-13 mb-3">
                                  Connect the Meta Ad Account
                                  used for your education
                                  advertising campaigns.
                                </div>

                                {loadingAdAccounts ? (
                                  <div className="py-3">

                                    <span
                                      className="spinner-border spinner-border-sm text-primary me-2"
                                    />

                                    <span className="text-muted fs-13">
                                      Loading available
                                      Ad Accounts...
                                    </span>

                                  </div>
                                ) : adAccounts.length ===
                                  0 ? (
                                  <div className="alert alert-warning mb-0">

                                    <div className="d-flex align-items-start">

                                      <FiAlertCircle
                                        size={20}
                                        className="me-2 flex-shrink-0"
                                      />

                                      <div>

                                        <strong>
                                          No Ad Accounts available
                                        </strong>

                                        <div className="fs-13 mt-1">
                                          No Meta Ad Account
                                          is currently available
                                          to this Meta account.
                                        </div>

                                        <button
                                          type="button"
                                          className="btn btn-outline-primary btn-sm mt-3"
                                          onClick={
                                            loadAdAccounts
                                          }
                                          disabled={
                                            loadingAdAccounts
                                          }
                                        >
                                          <FiRefreshCw className="me-1" />

                                          Reload Ad Accounts
                                        </button>

                                      </div>

                                    </div>

                                  </div>
                                ) : (
                                  <div className="row">

                                    <div className="col-md-8">

                                      <select
                                        className="form-select"
                                        value={
                                          selectedAdAccountId
                                        }
                                        onChange={(
                                          event
                                        ) =>
                                          setSelectedAdAccountId(
                                            event.target.value
                                          )
                                        }
                                        disabled={
                                          connectingAdAccount
                                        }
                                      >

                                        <option value="">
                                          Select a Meta Ad Account
                                        </option>

                                        {adAccounts.map(
                                          (
                                            account
                                          ) => (
                                            <option
                                              key={
                                                getAdAccountId(
                                                  account
                                                )
                                              }
                                              value={
                                                getAdAccountId(
                                                  account
                                                )
                                              }
                                            >
                                              {getAdAccountName(
                                                account
                                              )}

                                              {account?.currency
                                                ? ` — ${account.currency}`
                                                : ""}
                                            </option>
                                          )
                                        )}

                                      </select>

                                    </div>

                                    <div className="col-md-4 mt-2 mt-md-0">

                                      <button
                                        type="button"
                                        className="btn btn-primary w-100"
                                        onClick={
                                          handleConnectAdAccount
                                        }
                                        disabled={
                                          !selectedAdAccountId ||
                                          connectingAdAccount
                                        }
                                      >

                                        {connectingAdAccount ? (
                                          <>
                                            <span
                                              className="spinner-border spinner-border-sm me-2"
                                              role="status"
                                            />

                                            Connecting...
                                          </>
                                        ) : (
                                          <>
                                            <FiLink className="me-1" />

                                            Connect Ad Account
                                          </>
                                        )}

                                      </button>

                                    </div>

                                  </div>
                                )}

                              </>
                            )}

                          </div>

                        </div>

                      </div>

                      <hr className="my-4" />

                      {/* ========================================
                          CONNECTION INFORMATION
                      ========================================= */}

                      <div className="row">

                        <div className="col-md-3 mb-3 mb-md-0">

                          <div className="text-muted fs-12 mb-1">
                            Connection Status
                          </div>

                          <span className="badge bg-success-subtle text-success">
                            Connected
                          </span>

                        </div>

                        <div className="col-md-3 mb-3 mb-md-0">

                          <div className="text-muted fs-12 mb-1">
                            Last Lead Received
                          </div>

                          <div className="fw-semibold">
                            {formatDate(
                              status?.lastLeadReceivedAt
                            )}
                          </div>

                        </div>

                        <div className="col-md-3 mb-3 mb-md-0">

                          <div className="text-muted fs-12 mb-1">
                            Last Sync
                          </div>

                          <div className="fw-semibold">
                            {formatDate(
                              status?.lastSyncAt
                            )}
                          </div>

                        </div>

                        <div className="col-md-3">

                          <div className="text-muted fs-12 mb-1">
                            Meta Account
                          </div>

                          <div className="fw-semibold">
                            {metaUser?.name ||
                              metaUser?.username ||
                              "Connected"}
                          </div>

                        </div>

                      </div>

                    </div>

                    {/* ==========================================
                        LEADGEN ACTION
                    =========================================== */}

                    {!webhookSubscribed && (
                      <div className="border rounded p-3 bg-light mt-3">

                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">

                          <div>

                            <div className="fw-semibold">
                              Enable Facebook Leadgen
                            </div>

                            <div className="text-muted fs-13 mt-1">
                              Subscribe the connected Facebook
                              Page to the lead webhook so new
                              Meta education enquiries can enter
                              your lead management system.
                            </div>

                          </div>

                          <button
                            type="button"
                            className="btn btn-success"
                            onClick={
                              handleSubscribeLeadgen
                            }
                            disabled={
                              actionLoading
                            }
                          >

                            {actionLoading ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm me-2"
                                  role="status"
                                />

                                Processing...
                              </>
                            ) : (
                              <>
                                <FiCheckCircle className="me-1" />

                                Subscribe Leadgen
                              </>
                            )}

                          </button>

                        </div>

                      </div>
                    )}

                    {/* ==========================================
                        DISCONNECT
                    =========================================== */}

                    <div className="d-flex justify-content-end mt-3">

                      <button
                        type="button"
                        className="btn btn-outline-danger"
                        onClick={
                          handleDisconnect
                        }
                        disabled={
                          actionLoading
                        }
                      >

                        {actionLoading ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                            />

                            Disconnecting...
                          </>
                        ) : (
                          <>
                            <FiLogOut className="me-1" />

                            Disconnect Meta
                          </>
                        )}

                      </button>

                    </div>

                  </>
                )}

              </div>

            </div>

            {/* ==================================================
                CONNECTED FACEBOOK PAGE
            ================================================== */}

            {isConnected &&
              facebookConnected && (
                <div className="card shadow-sm border-0 mt-4">

                  <div className="card-header bg-transparent">

                    <div className="d-flex align-items-center justify-content-between">

                      <div>

                        <h5 className="card-title mb-1">
                          Connected Facebook Page
                        </h5>

                        <p className="text-muted mb-0 fs-13">
                          The Page currently connected to
                          your education organization.
                        </p>

                      </div>

                      <span className="badge bg-success-subtle text-success">
                        Connected
                      </span>

                    </div>

                  </div>

                  <div className="card-body">

                    <div className="row">

                      <div className="col-md-6 mb-3 mb-md-0">

                        <div className="text-muted fs-12 mb-1">
                          Page Name
                        </div>

                        <div className="fw-semibold">
                          {status?.facebook
                            ?.pageName ||
                            "Facebook Page"}
                        </div>

                      </div>

                      <div className="col-md-6">

                        <div className="text-muted fs-12 mb-1">
                          Page ID
                        </div>

                        <div className="fw-semibold">
                          {status?.facebook
                            ?.pageId ||
                            "—"}
                        </div>

                      </div>

                    </div>

                  </div>

                </div>
              )}

            {/* ==================================================
                AVAILABLE PAGES WHEN CONNECTED
            ================================================== */}

            {isConnected && (
              <div className="card shadow-sm border-0 mt-4">

                <div className="card-header bg-transparent">

                  <div className="d-flex align-items-center justify-content-between">

                    <div>

                      <h5 className="card-title mb-1">
                        Facebook Pages
                      </h5>

                      <p className="text-muted mb-0 fs-13">
                        Facebook Pages available to the
                        connected Meta account.
                      </p>

                    </div>

                    <span className="badge bg-primary-subtle text-primary">
                      {pages.length}{" "}
                      {pages.length === 1
                        ? "Page"
                        : "Pages"}
                    </span>

                  </div>

                </div>

                <div className="card-body">

                  {loadingPages ? (
                    <div className="text-center py-4">

                      <span
                        className="spinner-border spinner-border-sm text-primary"
                      />

                      <div className="text-muted fs-13 mt-2">
                        Loading Facebook Pages...
                      </div>

                    </div>
                  ) : pages.length === 0 ? (
                    <div className="text-center py-4">

                      <FiFacebook
                        size={34}
                        className="text-muted mb-3"
                      />

                      <h6>
                        No Facebook Pages found
                      </h6>

                      <button
                        type="button"
                        className="btn btn-light"
                        onClick={loadPages}
                      >
                        <FiRefreshCw className="me-1" />

                        Refresh Pages
                      </button>

                    </div>
                  ) : (
                    <div className="row">

                      {pages.map(
                        (page) => {
                          const pageId =
                            getPageId(page);

                          const isCurrentPage =
                            String(pageId) ===
                            String(
                              status?.facebook
                                ?.pageId || ""
                            );

                          return (
                            <div
                              className="col-xl-4 col-md-6 mb-3"
                              key={pageId}
                            >

                              <div
                                className={`border rounded p-3 h-100 ${
                                  isCurrentPage
                                    ? "border-success"
                                    : ""
                                }`}
                              >

                                <div className="d-flex align-items-center">

                                  <div
                                    className="rounded d-flex align-items-center justify-content-center flex-shrink-0"
                                    style={{
                                      width: 40,
                                      height: 40,
                                      background:
                                        "rgba(13, 110, 253, 0.1)",
                                    }}
                                  >
                                    <FiFacebook className="text-primary" />
                                  </div>

                                  <div className="ms-3 flex-grow-1">

                                    <div className="fw-semibold">
                                      {getPageName(page)}
                                    </div>

                                    <div className="text-muted fs-12">
                                      {pageId}
                                    </div>

                                  </div>

                                  {isCurrentPage && (
                                    <span className="badge bg-success-subtle text-success">
                                      Connected
                                    </span>
                                  )}

                                </div>

                              </div>

                            </div>
                          );
                        }
                      )}

                    </div>
                  )}

                </div>

              </div>
            )}

          </>
        )}

      </div>
    </div>
  );
};

export default Meta;